/* global __OFFLINE_PLUGIN_ENABLED__ */
import Vue from 'vue';
import VueMaterial from 'vue-material';
import VueRouter from 'vue-router';
import VueResource from 'vue-resource';
import GetTextPlugin from 'vue-gettext';
import * as OfflinePluginRuntime from 'offline-plugin/runtime';
import moment from 'moment';

import TimeBoard from './components/time-board.vue';
import TopicBoard from './components/topic-board.vue';
import MainView from './components/main-view.vue';
import PageNotFoundView from './components/page-not-found-view.vue';
import TaskView from './components/task-view.vue';
import TopbarDefault from './components/topbar-default.vue';
import { getCsrfToken, SessionPlugin } from './session.js';
import { getLanguage, setLanguageCookie } from './lang-util.js';

import translations from '../translations.json';

import '../css/app.scss';

performance.mark('app.js entry')
console.debug(performance.now().toFixed() + ': app.js entry');

if (typeof performance.measure('test measure', undefined, 'app.js entry') === 'undefined') {
  // performance.measure returns undefined. Firefox does that.
  // Anyway, let's just monkey patch it.
  let originalMeasure = performance.measure;
  performance.measure = function measure(measureName, startOrMeasureOptions, endMark) {
    originalMeasure.call(performance, measureName, startOrMeasureOptions, endMark);
    const measures = performance.getEntriesByName(measureName, 'measure');
    return measures[measures.length - 1]; // Return the last one.
  }
}

// I pack this boolean in to an extra object, so that Vue has something it can
// watch. MainView will take this object and put it into its data.
window.swInfo = {
  updateReady: false,
};

// Offline plugin (ServicWorker)
window.swUpdate = false;
if (__OFFLINE_PLUGIN_ENABLED__) {
  OfflinePluginRuntime.install({
    onUpdateReady() {
      console.log('Apply update');
      OfflinePluginRuntime.applyUpdate();
    },
    onUpdated() {
      console.log('Updated');
      window.swInfo.updateReady = true;
    },
  });
}

// Vue plugins
Vue.use(VueMaterial);
Vue.use(VueResource);
Vue.use(VueRouter);
Vue.use(SessionPlugin);

const initialLanguage = getLanguage();

// Translations.
Vue.use(GetTextPlugin, {
  availableLanguages: {
    en: 'English',
    pl: 'Polski',
  },
  defaultLanguage: initialLanguage,
  translations: translations,
  silent: true,
});

// Set locale for momentjs.
moment.locale(initialLanguage);

Vue.prototype.$setLanguage = function (lang) {
  if (!(lang in this.$language.available)) {
    throw Error(`Language '${lang}' not available.`);
  }
  setLanguageCookie(lang);
  // We can't reliably refresh all the components (especially where moments are
  // rendered), so let's just reload. We may try to improve that in the future,
  // but really changing the language is not the most common action.
  location.reload();
};

Vue.http.interceptors.push((request) => {
  if (!request.method || request.method.toLowerCase() != 'post') {
    return;
  }
  request.headers.set('X-CSRFToken', getCsrfToken());
});

const router = new VueRouter({
  base: '/app/',
  mode: 'history',
  routes: [
    {
      path: '/calendar',
      alias: '/',
      components: {
        default: TimeBoard,
        topbar: TopbarDefault,
      },
    },
    {
      path: '/lists/:layoutId',
      components: {
        default: TopicBoard,
        topbar: TopbarDefault,
      },
    },
    {
      path: '/task/:taskId',
      components: {
        default: TaskView,
        topbar: TopbarDefault,
      },
    },
    {
      path: '/tasks',
      redirect: '/calendar',
    },
    {
      // There is no 404 possible under /app/. We need to handle incorrect
      // addresses here instead.
      path: '*',
      components: {
        default: PageNotFoundView,
        topbar: TopbarDefault,
      },
    },
  ],
});

MainView.router = router;

document.getElementById('app').textContent = 'Initializing session...';

Vue.initSession(router).then(() => {
  console.debug(performance.now().toFixed() + ': Vue.initSession (promise/async)');
  performance.mark('Vue.initSession (promise/async)')
  performance.measure('1. initial loading', undefined, 'app.js entry')
  performance.measure('Processing to session', 'app.js entry', 'Vue.initSession (promise/async)')

  document.getElementById('app').textContent = 'Creating the UI...';

  // HERE IS THE IMPORTANT THING
  window.main = new Vue(MainView).$mount('#app');

  performance.mark('MainView mounted')
  performance.measure('Mounting MainView', 'Vue.initSession (promise/async)', 'MainView mounted')
  performance.measure('Total processing', 'app.js entry', 'MainView mounted')
  performance.measure('Total wait time', undefined, 'MainView mounted')
  if (performance.getEntriesByName('dashboard end').length) {
    performance.measure('Total to dashboard end?', undefined, 'dashboard end')
  }
  console.debug(performance.measure('Optimizable load time', 'app.js entry', 'MainView mounted'));

  const settings = window.main.$settings;
  if (settings?.labels?.length && !settings.labelMigrationDone) {
    setTimeout(() => {
      alert(
        "Your labels use old data format. " +
        "Please run label migration from the sidebar menu to update them."
      )
    }, 1000)
  }
});

performance.mark('Vue.initSession (sync)')
performance.measure('2. app.js initialization', 'app.js entry', 'Vue.initSession (sync)')